'use client';
import { useMemo, ReactNode } from 'react';
import Bookmark from '../common/bookmark';
import CopyLink from '../common/copy-link';
import { formatDate } from '@/helpers/date-format';
import getContentType from '@/helpers/get-content-type';
import { getLessonDurationDisplay, getLessonTypeLabel } from '@/lib/utils/lesson-meta';
import { useAuthUser } from '@/lib/auth/common/useSession';
import { Course, CourseLesson } from '@/types/course';
import { UserCourse, Lesson } from '@/types/user-course-lessons';
import { MarkdownAskAiToolbar } from '@/components/common/MarkdownAskAiToolbar';
import type { MarkdownExportSpec } from '@/lib/utils/markdown-export-path';

interface CourseMetaProps {
  course: Course;
  duration: string;
  userCourseData?: UserCourse | null;
  currentLesson?: Lesson | CourseLesson | null;
  userLessonData?: Lesson | null; // Lesson with user data (includes bookmark state)
  /** URL to copy when user clicks copy icon. When provided, copies this (course or lesson level) instead of current page. */
  copyUrl?: string;
  /** When set, “Copy for LLM” / “View as Markdown” render inline with the stats row. */
  markdownSpec?: MarkdownExportSpec | null;
}

interface MetaItem {
  value: string | ReactNode;
  show: boolean;
}

// Helper to safely get lesson duration (handles video, text, quiz)
const getLessonDuration = (lesson: Lesson | CourseLesson): string => {
  return getLessonDurationDisplay(lesson);
};

// Reusable Divider component
const Divider = () => (
  <div className="relative hidden md:block">
    <span className="divider" />
  </div>
);

// Reusable MetaItem component
const MetaItemWithDivider = ({
  children,
  showDivider = true,
}: {
  children: ReactNode;
  showDivider?: boolean;
}) => (
  <>
    {children && <span className="cs-caption">{children}</span>}
    {showDivider && children && <Divider />}
  </>
);

export default function CourseMeta({
  course,
  duration,
  userCourseData,
  currentLesson,
  userLessonData,
  copyUrl,
  markdownSpec,
}: CourseMetaProps) {
  const isLessonView = Boolean(currentLesson);
  const { isLoggedIn, isLoading: authLoading } = useAuthUser();

  // Get bookmark state - prioritize lesson bookmark if on lesson view
  const { isBookmarked, lessonUid } = useMemo(() => {
    if (isLessonView && userLessonData) {
      // Lesson view: use userLessonData's bookmark state
      return {
        isBookmarked: userLessonData.isBookmarked || false,
        lessonUid: userLessonData.entryUid,
      };
    }
    // Course view: use course's bookmark state. Falls through to the default (unbookmarked)
    // while auth is still resolving, same as while genuinely logged out.
    if (!authLoading && isLoggedIn && userCourseData) {
      return {
        isBookmarked: userCourseData.isBookmarked,
        lessonUid: undefined,
      };
    }
    return { isBookmarked: false, lessonUid: undefined };
  }, [isLessonView, userLessonData, userCourseData, isLoggedIn, authLoading]);

  // Build metadata items (DRY approach)
  const metaItems: MetaItem[] = useMemo(() => {
    if (isLessonView && currentLesson) {
      // Lesson metadata: show content type (Quiz, Text Lesson, Video Lesson) and duration
      const contentType = getLessonTypeLabel(currentLesson);
      return [
        {
          value: contentType,
          show: true,
        },
        {
          value: getLessonDuration(currentLesson),
          show: Boolean(getLessonDuration(currentLesson)),
        },
        {
          value: course?.right_rail?.expertise_levels,
          show: Boolean(course?.right_rail?.expertise_levels),
        },
        {
          value: `Released: ${formatDate(course?.right_rail?.release_date) || formatDate(course.created_at)}`,
          show: Boolean(course?.right_rail?.release_date) ||  Boolean(course.created_at),
        },
      ];
    }

    // Course metadata: content type, duration, expertise level, release date
    const taxonomies = course?.taxonomies ?? [];
    const contentTypeFromTax = getContentType(taxonomies, 'content_type');
    const contentTypeValue = contentTypeFromTax || (course?.is_standalone ? 'Content' : 'Course');
    return [
      {
        value: contentTypeValue,
        show: Boolean(contentTypeValue),
      },
      {
        value: duration,
        show: Boolean(duration),
      },
      {
        value: course.right_rail.expertise_levels,
        show: Boolean(course?.right_rail?.expertise_levels),
      },
      {
        value: `Released: ${formatDate(course.right_rail.release_date) || formatDate(course.created_at)}`,
        show: Boolean(course.right_rail.release_date) ||  Boolean(course.created_at),
      },
    ];
  }, [isLessonView, currentLesson, course, duration]);

  const visibleMetaItems = metaItems.filter((item) => item.show);
  const title = isLessonView && currentLesson ? currentLesson.title : course.title;
  

  return (
    <div className="bg-surface-3 w-inherit flex flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between md:p-8">
      <div className="flex-1">
        <h1 className="cs-h2 text-text-default-strong mb-4">{title}</h1>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-2 py-1 md:gap-2">
          {visibleMetaItems.map((item, index) => (
            <MetaItemWithDivider key={index} showDivider={index < visibleMetaItems.length - 1}>
              {item.value}
            </MetaItemWithDivider>
          ))}
          {markdownSpec ? (
            <>
              {visibleMetaItems.length > 0 ? <Divider /> : null}
              <MarkdownAskAiToolbar spec={markdownSpec} variant="inline" />
            </>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-2 self-end">
        <Bookmark 
          isBookmarkedBool={isBookmarked} 
          courseEntryUid={course.uid}
          lessonEntryUid={lessonUid}
        />
        <CopyLink url={copyUrl} />
      </div>
    </div>
  );
}
